// Contact secret: typing the trigger (data-trigger, "119") as the name on the contact page opens a full-screen
// "incoming transmission" over code rain. The words come from the hidden page the merchant controls (handle 119,
// rendered into the <template data-transmission-text>), one line at a time in the terminal type, letter by letter
// with a cursor. Lines that come back (a chorus) glow brighter. Skip shows everything at once; Close or Esc ends it.
// The view follows the newest line unless you scroll up to read; scroll back down and it follows again.
//
// Sound: the <audio data-transmission-audio> tracks play as one recording: the short intro clip, then the LAST CALL
// recording, which cuts in data-handoff seconds into the intro (where the intro's last phrase repeats at the start of
// the recording) with a short crossfade, so the phrase is heard once and the intro's abrupt stop is never heard.
// The words type at their own reading pace. The speed button (1x, 2x, 4x) fast
// forwards the words and the recording together. A voice meter in the header moves with the sound. If the sound
// can't play, the words run on their own.
const CHAR_MS = 30.4; // terminal typing (medium pace, then 25% faster)
const READING_WPM = 300; // reading time allowed for every line
const LINE_GAP_MS = 480; // minimum pause after the line finishes
const SPEEDS = [1, 2, 4]; // the speed button cycles through these
const METER_BARS = 13;
const FADE_S = 0.3; // crossfade length where the intro hands over to the recording
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

class STransmission extends HTMLElement {
  connectedCallback() {
    this.trigger = (this.dataset.trigger || '').trim().toLowerCase();
    this.dialog = this.querySelector('dialog');
    this.screen = this.querySelector('[data-transmission-lines]');
    this.tracks = [...this.querySelectorAll('audio[data-transmission-audio]')].filter((audio) => audio.getAttribute('src'));
    this.meter = this.querySelector('canvas[data-transmission-meter]');
    this.lines = this.readLines();
    if (!this.trigger || !this.dialog || !this.screen || !this.lines.length) return;

    this.onInput = (event) => {
      const input = event.target;
      if (!input.matches?.(this.dataset.input || '#contact-name')) return;
      if (input.value.trim().toLowerCase() !== this.trigger) return;
      // The secret is not a name: clear the box so it is never sent with the form.
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.blur();
      this.open();
    };
    document.addEventListener('input', this.onInput, true);

    this.querySelector('[data-transmission-skip]')?.addEventListener('click', () => this.showAll());
    this.speedButton = this.querySelector('[data-transmission-speed]');
    this.speedButton?.addEventListener('click', () => this.setSpeed(SPEEDS[(SPEEDS.indexOf(this.speed) + 1) % SPEEDS.length]));
    this.setSpeed(1);
    this.querySelectorAll('[data-transmission-close]').forEach((button) => button.addEventListener('click', () => this.dialog.close()));
    this.dialog.addEventListener('close', () => {
      this.stop();
      this.stopSound();
    });
    this.screen.addEventListener('scroll', () => {
      const { scrollTop, scrollHeight, clientHeight } = this.screen;
      this.follow = scrollHeight - scrollTop - clientHeight < 40;
    }, { passive: true });

    // One recording: each track hands over to the next when it ends.
    this.tracks.forEach((audio, i) => {
      audio.addEventListener('ended', () => {
        const next = this.tracks[i + 1];
        if (!next || !this.dialog.open || this.handedOff) return;
        this.handedOff = true;
        this.setGain(i + 1, 1);
        this.playTrack(next, this.skip);
      });
    });
  }

  disconnectedCallback() {
    if (this.onInput) document.removeEventListener('input', this.onInput, true);
    this.stop();
    this.stopSound();
  }

  // The page's text, one entry per line (paragraphs and line breaks both start a new line); blank lines dropped.
  readLines() {
    const source = this.querySelector('template[data-transmission-text]');
    if (!source) return [];
    const holder = document.createElement('div');
    holder.append(source.content.cloneNode(true));
    holder.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    holder.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6').forEach((block) => block.append('\n'));
    const lines = holder.textContent.split('\n').map((line) => line.replace(/\s+/g, ' ').trim()).filter(Boolean);
    // A line heard more than once is part of a chorus.
    const counts = new Map();
    for (const line of lines) counts.set(line.toLowerCase(), (counts.get(line.toLowerCase()) || 0) + 1);
    return lines.map((text) => ({ text, chorus: counts.get(text.toLowerCase()) > 1 }));
  }

  open() {
    this.stop();
    this.stopSound();
    this.screen.replaceChildren();
    this.follow = true;
    this.querySelector('[data-transmission-skip]')?.removeAttribute('hidden');
    this.speedButton?.removeAttribute('hidden');
    this.setSpeed(1);
    this.dialog.showModal();
    this.screen.scrollTop = 0;
    this.startSound();
    if (reduced.matches) {
      this.showAll();
      return;
    }
    this.index = 0;
    this.next();
  }

  // Sound
  startSound() {
    if (!this.tracks.length) return;
    this.tracks.forEach((audio) => {
      audio.preload = 'auto';
      try { audio.currentTime = 0; } catch { /* not loaded yet */ }
    });
    this.connectMeter();
    this.handoff = parseFloat(this.dataset.handoff);
    // Where the recording picks up: past the words the intro already said, so nothing repeats.
    this.skip = parseFloat(this.dataset.skip) || 0;
    this.handedOff = false;
    this.tracks.forEach((_, i) => this.setGain(i, 1));
    this.playTrack(this.tracks[0]);
    this.drawMeter();
  }

  playTrack(audio, from = 0) {
    audio.playbackRate = this.speed;
    audio.preservesPitch = true;
    try { audio.currentTime = from; } catch { /* not loaded yet */ }
    Promise.resolve(audio.play()).catch(() => { /* sound blocked: the words carry on by themselves */ });
  }

  stopSound() {
    this.tracks?.forEach((audio) => audio.pause());
    cancelAnimationFrame(this.meterFrame);
    this.meterFrame = 0;
    this.paintMeter(null);
  }

  // Voice meter: every track feeds one analyser (the browser allows this once per track, so it is wired up on the
  // first open, which comes from typing and so may start sound). If the analyser can't be made, the sound still plays.
  connectMeter() {
    if (this.analyser || !this.meter) {
      this.audioContext?.resume?.();
      return;
    }
    try {
      const Context = window.AudioContext || window.webkitAudioContext;
      if (!Context) return;
      this.audioContext = new Context();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.72;
      // Each track has its own volume (for the crossfade), all feeding the meter.
      this.gains = this.tracks.map((audio) => {
        const gain = this.audioContext.createGain();
        this.audioContext.createMediaElementSource(audio).connect(gain);
        gain.connect(this.analyser);
        return gain;
      });
      this.analyser.connect(this.audioContext.destination);
      this.levels = new Uint8Array(this.analyser.frequencyBinCount);
      this.audioContext.resume?.();
    } catch {
      this.analyser = null;
    }
  }

  setGain(i, value) {
    const gain = this.gains?.[i];
    if (!gain) return;
    gain.gain.cancelScheduledValues(this.audioContext.currentTime);
    gain.gain.setValueAtTime(value, this.audioContext.currentTime);
  }

  // Handoff: once the intro reaches data-handoff seconds, the recording starts and the two crossfade.
  checkHandoff() {
    const [first, next] = this.tracks;
    if (!next || this.handedOff || !Number.isFinite(this.handoff)) return;
    if (first.paused || first.currentTime < this.handoff) return;
    this.handedOff = true;
    const fade = FADE_S / this.speed;
    if (this.gains && this.audioContext) {
      const now = this.audioContext.currentTime;
      const [out, into] = this.gains;
      out.gain.cancelScheduledValues(now);
      out.gain.setValueAtTime(1, now);
      out.gain.linearRampToValueAtTime(0, now + fade);
      into.gain.cancelScheduledValues(now);
      into.gain.setValueAtTime(0, now);
      into.gain.linearRampToValueAtTime(1, now + fade);
      this.playTrack(next, this.skip);
      setTimeout(() => first.pause(), fade * 1000 + 60);
    } else {
      first.pause();
      this.playTrack(next, this.skip);
    }
  }

  drawMeter() {
    if (!this.meter) return;
    cancelAnimationFrame(this.meterFrame);
    const frame = () => {
      if (!this.dialog.open) return;
      this.checkHandoff();
      const playing = this.tracks.some((audio) => !audio.paused && !audio.ended);
      let bars = null;
      if (playing && this.analyser) {
        this.analyser.getByteFrequencyData(this.levels);
        // Voice range: the low-mid bins (about 90 Hz to 3.5 kHz at 44.1-48 kHz), split into bands.
        const from = 1;
        const to = Math.min(this.levels.length, 34);
        const band = (to - from) / METER_BARS;
        bars = [];
        for (let b = 0; b < METER_BARS; b++) {
          let peak = 0;
          for (let i = Math.floor(from + b * band); i < Math.floor(from + (b + 1) * band); i++) peak = Math.max(peak, this.levels[i]);
          bars.push(Math.max(0, (peak - 60) / 170));
        }
      } else if (playing) {
        // No analyser: a gentle idle pulse so the meter still shows the sound is on.
        const t = performance.now() / 260;
        bars = Array.from({ length: METER_BARS }, (_, b) => 0.25 + 0.2 * Math.sin(t + b * 0.9));
      }
      this.paintMeter(bars);
      this.meterFrame = requestAnimationFrame(frame);
    };
    this.meterFrame = requestAnimationFrame(frame);
  }

  // Bars of stacked terminal blocks in the accent green, mirrored from the middle like a voice wave, with a bright
  // cap on each bar that falls back slowly (peak hold). Flat dots when silent.
  paintMeter(bars) {
    const canvas = this.meter;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = canvas.clientWidth || 104;
    const height = canvas.clientHeight || 30;
    if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    this.meterColor ??= getComputedStyle(this).getPropertyValue('--color-accent').trim() || '#00ff66';
    this.peaks ??= new Float32Array(METER_BARS);
    const gap = 2;
    const barWidth = (width - gap * (METER_BARS - 1)) / METER_BARS;
    const block = 2;
    const step = block + 1;
    const half = Math.floor(height / 2 / step); // blocks above (and below) the middle line
    const middle = height / 2;
    ctx.shadowColor = this.meterColor;
    ctx.shadowBlur = 6;
    for (let b = 0; b < METER_BARS; b++) {
      // Middle bars lean taller, so it reads as a voice wave.
      const shape = 0.5 + 0.5 * Math.sin(((b + 0.5) / METER_BARS) * Math.PI);
      const level = bars ? Math.min(1, bars[b] * shape * 1.25) : 0;
      this.peaks[b] = bars ? Math.max(level, this.peaks[b] - 0.018) : 0;
      const lit = Math.max(1, Math.round(level * half));
      const x = b * (barWidth + gap);
      ctx.fillStyle = this.meterColor;
      for (let k = 0; k < lit; k++) {
        ctx.globalAlpha = 1 - (k / Math.max(1, half)) * 0.35;
        ctx.fillRect(x, middle - (k + 1) * step + 1, barWidth, block);
        ctx.fillRect(x, middle + k * step, barWidth, block);
      }
      // Peak cap: a bright block where the bar last reached.
      const cap = Math.round(this.peaks[b] * half);
      if (bars && cap > lit) {
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#eaffea';
        ctx.fillRect(x, middle - cap * step + 1, barWidth, block);
        ctx.fillRect(x, middle + (cap - 1) * step, barWidth, block);
      }
    }
    ctx.globalAlpha = 1;
  }

  // Speed button: 1x, 2x, 4x; its label shows the current speed. It fast forwards the recording too.
  setSpeed(speed) {
    this.speed = speed;
    this.tracks?.forEach((audio) => { audio.playbackRate = speed; });
    if (!this.speedButton) return;
    this.speedButton.querySelector('[data-speed-value]').textContent = `${speed}x`;
    this.speedButton.setAttribute('aria-pressed', String(speed > 1));
  }

  stop() {
    clearTimeout(this.timer);
    this.timer = null;
    this.cursor?.remove();
  }

  addLine(line) {
    const row = document.createElement('p');
    row.className = 'transmission-line';
    if (line.chorus) row.classList.add('is-chorus');
    this.screen.append(row);
    return row;
  }

  // Types the next line letter by letter, then pauses and moves on.
  next() {
    if (this.index >= this.lines.length) {
      this.done();
      return;
    }
    const line = this.lines[this.index++];
    const row = this.addLine(line);
    const text = document.createTextNode('');
    this.cursor ??= Object.assign(document.createElement('span'), { className: 'transmission-cursor', textContent: '█' });
    row.append(text, this.cursor);
    const readingTime = line.text.trim().split(/\s+/).length * 60000 / READING_WPM;
    const pause = Math.max(LINE_GAP_MS, readingTime - line.text.length * CHAR_MS) + (line.chorus ? 400 : 0);
    let shown = 0;
    // One letter per tick; the speed is read on every tick, so the button takes effect straight away.
    const type = () => {
      shown++;
      text.data = line.text.slice(0, shown);
      this.keepUp();
      if (shown < line.text.length) this.timer = setTimeout(type, CHAR_MS / this.speed);
      else this.timer = setTimeout(() => this.next(), pause / this.speed);
    };
    type();
  }

  // Skip: every line at once. The recording keeps playing.
  showAll() {
    this.stop();
    this.screen.replaceChildren();
    for (const line of this.lines) this.addLine(line).textContent = line.text;
    this.done();
  }

  done() {
    this.index = this.lines.length;
    this.cursor ??= Object.assign(document.createElement('span'), { className: 'transmission-cursor', textContent: '█' });
    const end = document.createElement('p');
    end.className = 'transmission-line transmission-end';
    end.textContent = this.dataset.endText || '';
    end.append(this.cursor);
    this.screen.append(end);
    this.querySelector('[data-transmission-skip]')?.setAttribute('hidden', '');
    this.speedButton?.setAttribute('hidden', '');
    this.keepUp();
  }

  keepUp() {
    if (this.follow) this.screen.scrollTop = this.screen.scrollHeight;
  }
}

if (!customElements.get('s-transmission')) customElements.define('s-transmission', STransmission);
